import React, {useState, useEffect} from 'react';
import {Product} from '../models/product.model';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import AppTableHeaderCell from '../../../Presentation/components/ui/AppTableHeaderCell';
import {AppText, AppBodyText, AppSmallText} from '../../../Presentation/components/ui/Typography';
import {Trash2, Plus, Eye, Edit} from 'lucide-react';
import Modal from '../../../Presentation/components/ui/Modal';
import Button from '../../../Presentation/components/ui/Button';
import {ProductTableProps} from '../types/table.types';
import {useProductOperations} from '../hooks/useProductOperations';
import {LoggerService} from '../../../Services/common/LoggerService';
import {InventoryAPIDataSource} from '../../inventory/api/InventoryAPIDataSource';
import {useProducts} from '../hooks/useProducts';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormat} from '../../../types/export.types';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';

const ProductsTableV2: React.FC<ProductTableProps> = ({
  data: products,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onAddStock,
  onViewDetails,
  onEditProduct,
  currentUserId: _currentUserId, // Unused but required by interface
  showPageSizeSelector = true,
  showPaginationInfo = true,
  exportable = false,
  loading = false
}) => {
  const {hasPermission} = useAuthorization();
  const [deleteProduct, setDeleteProduct] = useState<Product | null>(null);
  const {deleteProduct: deleteProductMutation} = useProductOperations();
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [loadingIds, setLoadingIds] = useState<Record<number, boolean>>({});
  const {tags: globalTags, fetchTags} = useProducts({autoFetch: false});

  // Export configuration
  const {createConfig} = useExportConfig<Product>();

  const exportConfig = createConfig([
    {key: 'id', title: 'معرف المنتج'},
    {key: 'name', title: 'اسم المنتج'},
    {key: 'type', title: 'النوع'},
    {key: 'barcode', title: 'الباركود'},
    {key: 'brandName', title: 'العلامة التجارية'},
    {key: 'serialCount', title: 'عدد السيريالات'},
    {key: 'tags', title: 'العلامات', formatter: ExportFormatters.tags},
    {key: 'price', title: 'السعر', formatter: ExportFormatters.currencySARExport},
    {key: 'hasSerial', title: 'نوع المنتج', formatter: ExportFormatters.serialType}
  ], 'منتجات', 'تصدير بيانات المنتجات', 'تصدير جميع بيانات المنتجات مع تفاصيلها');

  // Export hook
  const {exportData, isExporting, canExport, recordCount} = useTableExport({
    data: products,
    config: exportConfig
  });

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    await exportData(format);
  };

  useEffect(() => {
    if (!globalTags || globalTags.length === 0) {
      fetchTags();
    }
  }, []);

  // Fetch quantities for visible products (lazy, cached per mount)
  useEffect(() => {
    const fetchForProduct = async (productId: number) => {
      try {
        setLoadingIds(prev => ({...prev, [productId]: true}));
        const res = await InventoryAPIDataSource.getProductInventoryStatus(String(productId));
        const arr = Array.isArray(res) ? res : [res];
        const dto = arr.find((s: any) => String(s.product_id) === String(productId));
        const total = dto?.total_quantity ?? (
          Array.isArray(dto?.locations)
            ? dto.locations.reduce((sum: number, loc: any) => sum + (Number(loc?.quantity) || 0), 0)
            : 0
        );
        setQuantities(prev => ({...prev, [productId]: Number(total) || 0}));
      } catch (e) {
        LoggerService.warn && LoggerService.warn('Failed to fetch product quantity', {productId, error: e}, 'ProductsTableV2');
        setQuantities(prev => ({...prev, [productId]: 0}));
      } finally {
        setLoadingIds(prev => ({...prev, [productId]: false}));
      }
    };

    products.forEach(p => {
      if (quantities[p.id] === undefined && !loadingIds[p.id]) {
        fetchForProduct(p.id);
      }
    });
  }, [products]);

  const handleDelete = async () => {
    if (!deleteProduct) {return;}

    try {
      await deleteProductMutation.mutateAsync(deleteProduct.id);
      setDeleteProduct(null);
    } catch (error) {
      LoggerService.error('Delete product failed', error, 'ProductsTableV2');
    }
  };

  const handleAddStock = (product: Product) => {
    if (onAddStock) {
      onAddStock(product);
    }
  };

  const handleViewDetails = (product: Product) => {
    if (onViewDetails) {
      onViewDetails(product);
    }
  };

  // Enhanced columns with clear headers - removed stock and categories columns
  const columns: TableColumn<Product>[] = [
    {
      key: 'name',
      title: <AppTableHeaderCell label="اسم المنتج" />,
      render: (product: Product) => (
        <div className="py-3">
          <AppText className="font-medium text-gray-900">{product.name}</AppText>
        </div>
      )
    },
    {
      key: 'barcode',
      title: <AppTableHeaderCell label="الباركود" />,
      render: (product: Product) => (
        <div className="py-3">
          <AppText className="font-mono text-gray-700">{product.barcode}</AppText>
        </div>
      )
    },
    {
      key: 'brand',
      title: <AppTableHeaderCell label="البراند" />,
      render: (product: Product) => (
        <div className="py-3">
          <AppText className="text-gray-700">{product.brandName}</AppText>
        </div>
      )
    },
    {
      key: 'type',
      title: <AppTableHeaderCell label="النوع" />,
      render: (product: Product) => (
        <div className="py-3">
          <AppText className="text-gray-700">{product.type || 'غير محدد'}</AppText>
        </div>
      )
    },
    {
      key: 'tags',
      title: <AppTableHeaderCell label="الوسوم" />,
      render: (product: Product) => (
        <div className="py-3">
          {product.tags && product.tags.length > 0 ? (
            <AppBodyText className="text-gray-700">
              {product.tags
                .map(t => {
                  const num = Number(t);
                  if (!Number.isNaN(num)) {
                    const found = (globalTags || []).find((gt: any) => gt.id === num);
                    return found?.name || t;
                  }
                  // sometimes backend puts tag name in slug; try resolve by slug
                  const bySlug = (globalTags || []).find((gt: any) => gt.slug === t);
                  return bySlug?.name || t;
                })
                .join(', ')}
            </AppBodyText>
          ) : (
            <AppSmallText className="text-gray-400">لا توجد</AppSmallText>
          )}
        </div>
      )
    },
    {
      key: 'hasSerial',
      title: <AppTableHeaderCell label="نوع المخزون" />,
      render: (product: Product) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          product.hasSerial ? 'bg-brand-100 text-brand-800' : 'bg-green-100 text-green-800'
        }`}>
          {product.hasSerial ? 'بسيريال' : 'بدون سيريل'}
        </span>
      )
    },
    {
      key: 'amount',
      title: <AppTableHeaderCell label="الكمية" />,
      render: (product: Product) => (
        <div className="py-3">
          {quantities[product.id] !== undefined ? (
            <div className="flex items-center gap-2">
              <AppText className="font-medium text-gray-900">{quantities[product.id]}</AppText>
              {product.hasSerial && (
                <span className="text-xs bg-brand-100 text-brand-800 px-1 rounded">سيريال</span>
              )}
            </div>
          ) : (
            <AppText className="text-gray-400">جاري التحميل...</AppText>
          )}
        </div>
      )
    },
    {
      key: 'price',
      title: <AppTableHeaderCell label="السعر" />,
      render: (product: Product) => (
        <div className="py-3">
          <AppText className="font-medium text-gray-900">{formatCurrency(product.price)}</AppText>
        </div>
      )
    }

  ];

  return (
    <>
      {/* Export Button */}
      {exportable && (
        <div className="mb-4 flex justify-end">
          <ExportButton
            onExport={handleExport}
            isExporting={isExporting}
            canExport={canExport}
            recordCount={recordCount}
            showDropdown={true}
          />
        </div>
      )}

      <AppTable
        columns={columns}
        data={products}
        rowKey={(product, index) => `product-${product.id}-${index || 0}`}
        emptyText="لا توجد منتجات"
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showPageSizeSelector={showPageSizeSelector}
        showPaginationInfo={showPaginationInfo}
        loading={loading}
        actions={(product) => {
          const actions = [];

          // View Details - Basic permission
          if (hasPermission(PERMISSIONS.VIEW_DASHBOARD)) {
            actions.push({
              label: 'عرض التفاصيل',
              icon: <Eye className="text-brand-600" size={20} />,
              onClick: () => handleViewDetails(product)
            });
          }

          // Add Stock - Requires inventory create permission
          if (hasPermission(PERMISSIONS.CREATE_INVENTORY) || hasPermission(PERMISSIONS.MANAGE_INVENTORY)) {
            actions.push({
              label: 'إضافة مخزون',
              icon: <Plus className="text-green-600" size={20} />,
              onClick: () => handleAddStock(product)
            });
          }

          // Edit Product - Requires edit permission
          if (hasPermission(PERMISSIONS.MANAGE_PRODUCTS)) {
            actions.push({
              label: 'تعديل المنتج',
              icon: <Edit className="text-yellow-600" size={20} />,
              onClick: () => onEditProduct?.(product)
            });
          }

          // Delete Product - Requires delete permission
          if (hasPermission(PERMISSIONS.MANAGE_PRODUCTS)) {
            actions.push({
              label: 'حذف المنتج',
              icon: <Trash2 className="text-red-600" size={20} />,
              onClick: () => setDeleteProduct(product),
              danger: true
            });
          }

          return actions;
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteProduct}
        onClose={() => setDeleteProduct(null)}
        title="تأكيد الحذف"
        width="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            هل أنت متأكد من حذف المنتج "{deleteProduct?.name}"؟
          </p>
          <p className="text-sm text-red-600">
            هذا الإجراء لا يمكن التراجع عنه.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="ghost" onClick={() => setDeleteProduct(null)} disabled={deleteProductMutation.isPending}>
              إلغاء
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deleteProductMutation.isPending}>
              حذف
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ProductsTableV2;
